import { createCheckoutSession } from "@/api/privateApi";
import { CTAButton } from "@/components/Button";
import {
  navigationSquadBuilderIcon,
  navigationTacticsIcon,
} from "@/components/organisms/NavigationBar/NavBarTouchDevice/components/NavIcons";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { storeSignupContext } from "@/events/signupTracking";
import { club } from "@/icons";
import {
  cleanTouchBarMenuZIndex,
  setTouchBarMenuZIndex,
} from "@/redux/globalState/globalStateSlice";
import { addAlert, showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { getUserSelector, setBuyIntent } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import {
  backendErrorCode,
  errorMessage,
  useTranslation,
} from "@repo/shared/i18n";
import { Dna } from "lucide-react";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { USP } from "../LandingPages/Features/BaseLandingPage/USP";
import { sbcSolverIcon } from "../LandingPages/Features/SBCSolverLandingPage";

/*

If you want to create a new landing page, you just add an entry below in the switch statement.

It should be pretty straight forward. Now, in order for the users to land on tots, the URL looks like this: https://easysbc.com/get-ultimate?v=tots

So, if you make a new one, you just add v=your-new-landing-page-type to the URL and add it to the switch statement.
*/

type LandingPageType =
  | "generic"
  | "tots"
  | "tots-free"
  | "google-paid-offer"
  | "tots-deal";

type LandingPageCopy = "generic" | "totsFree" | "googlePaidOffer" | "totsDeal";

interface LandingPageProps {
  /** Key under `landing:getUltimate.variants` holding title, mobileTitle and description. */
  copy: LandingPageCopy;
  promoCodeId?: string;
  actualCode?: string;
  couponId?: string; // This is important, because if we use promo codes they can also be applied to yearly ultimate
}

const LANDING_PAGE_TYPE = ({
  type,
}: {
  type: LandingPageType;
}): LandingPageProps => {
  switch (type) {
    case "generic":
      return {
        copy: "generic",
        promoCodeId: "promo_1TBtjPATqmUvIGSpwRrdNtmI",
        actualCode: "FC26Evos",
      };
    case "tots-free":
      return {
        copy: "totsFree",
        actualCode: "TOTS_2026",
        promoCodeId: "promo_1TOGiiATqmUvIGSp9LHci2VE",
      };
    case "google-paid-offer":
      return {
        copy: "googlePaidOffer",
        promoCodeId: "promo_1TVsObATqmUvIGSpti19i6In",
      };
    case "tots-deal":
      return {
        copy: "totsDeal",
        promoCodeId: "promo_1TZQquATqmUvIGSpai9hNgBM",
        actualCode: "TOTSDEAL",
      };
    default:
      return {
        copy: "generic",
        promoCodeId: "promo_1TBtjPATqmUvIGSpwRrdNtmI",
        actualCode: "FC26Evos",
      };
  }
};

export const GetUltimateLandingPage = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const { user } = useSelector(getUserSelector);

  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const type = searchParams.get("v") as LandingPageType;
  const { t } = useTranslation("landing");

  const { copy, promoCodeId, couponId } = LANDING_PAGE_TYPE({
    type: type || "generic",
  });
  const title = t(`getUltimate.variants.${copy}.title` as const);
  const mobileTitle = t(`getUltimate.variants.${copy}.mobileTitle` as const);
  const description = t(`getUltimate.variants.${copy}.description` as const);

  const USPs = [
    {
      icon: <div className="size-6 touch-device:size-10 shrink-0">{club}</div>,
      title: t("getUltimate.usp.clubImport.title"),
      description: (
        <div className="text-sm mobile:text-lg">
          {t("getUltimate.usp.clubImport.description")}
        </div>
      ),
      newTag: true,
    },
    {
      icon: (
        <div className="size-6 mobile:size-10 shrink-0">
          {navigationSquadBuilderIcon}
        </div>
      ),
      title: t("getUltimate.usp.aiSquadBuilder.title"),
      description: (
        <div className="text-sm mobile:text-lg">
          {" "}
          {t("getUltimate.usp.aiSquadBuilder.description")}
        </div>
      ),
    },
    {
      icon: (
        <div className="size-6 mobile:size-10 shrink-0">
          <Dna className="size-6 mobile:size-10" />
        </div>
      ),
      title: t("getUltimate.usp.evolutions.title"),
      description: (
        <div className="text-sm mobile:text-lg">
          {t("getUltimate.usp.evolutions.description")}
        </div>
      ),
    },
    {
      icon: (
        <div className="size-6 mobile:size-10 shrink-0">
          {navigationTacticsIcon}
        </div>
      ),
      title: t("getUltimate.usp.premiumTactics.title"),
      description: (
        <div className="text-sm touch-device:text-lg">
          {t("getUltimate.usp.premiumTactics.description")}
        </div>
      ),
    },
    {
      icon: (
        <div className="size-6 mobile:size-10 shrink-0">{sbcSolverIcon}</div>
      ),
      title: t("getUltimate.usp.sbcSolver.title"),
      description: (
        <div className="text-sm touch-device:text-lg">
          {t("getUltimate.usp.sbcSolver.description")}
        </div>
      ),
      newTag: true,
    },
  ];

  const handleClickCTA = () => {
    // Add discount id when setup in Stripe

    dispatch(
      setBuyIntent({
        subscriptionId: "diamond_monthly",
        promoCode: promoCodeId,
        discountId: couponId,
      }),
    );

    if (!user?.uuid) {
      // The plain "login" modal doesn't set signup context itself (unlike the
      // "login-required" modal), so record it here — otherwise a signup started
      // from this page would inherit a stale trigger from an earlier gate.
      storeSignupContext("cta", {
        section: "Get Ultimate Landing Page",
        subsection: type || "generic",
      });
      dispatch(showModal({ modalType: "login" }));
    } else if (user?.subscription === "free") {
      // create checkout session
      createCheckoutSession({
        priceId: "diamond_monthly",
        promoCode: promoCodeId,
        discountId: couponId,
      }).catch((error) =>
        dispatch(
          addAlert({
            description: errorMessage(backendErrorCode(error)),
            type: "error",
          }),
        ),
      );
    } else {
      dispatch(
        addAlert({
          description: t("getUltimate.alreadyPayingCustomer"),
          type: "info",
        }),
      );
      navigate("/my-club");
    }
  };

  useEffect(() => {
    mixpanelTrack("landed_on_get_ultimate_page");
    dispatch(setTouchBarMenuZIndex("z-30"));

    return () => {
      dispatch(cleanTouchBarMenuZIndex());
    };
  }, [dispatch]);

  return isMobile ? (
    <main
      ref={pageRef}
      className="bg-panel-base relative min-h-screen overflow-x-hidden pb-12"
    >
      <img
        className="fixed bottom-0 inset-x-0 m-auto z-0 opacity-10 pointer-events-none"
        src={"/logo.png"}
        alt={t("getUltimate.logoAlt")}
      />
      <div className="relative z-20 flex flex-col min-h-screen px-6 pt-8 small-mobile:pt-12">
        <div className="flex flex-col gap-y-8 flex-1">
          <h1 className="text-4xl font-bold">{mobileTitle}</h1>
          <h2 className="text-secondary/80 font-medium">{description}</h2>
          <div className="small-mobile:mt-6">
            <CTAButton
              onClick={handleClickCTA}
              label={t("getUltimate.ctaDiscount")}
            />
          </div>
        </div>

        <h2 className="text-3xl font-semibold mb-10 mt-10">
          {t("getUltimate.whyEasySbc")}
        </h2>
        <ul className="flex flex-col gap-y-4">
          {USPs.map((usp) => (
            <USP key={usp.title} {...usp} />
          ))}
        </ul>
        <div className="mt-10">
          <CTAButton
            onClick={handleClickCTA}
            label={t("getUltimate.ctaBecomeUltimate")}
          />
        </div>
      </div>
    </main>
  ) : (
    <main className="bg-panel-base relative overflow-x-hidden pb-24">
      <div className="relative z-20 flex flex-col min-h-[calc(100vh-50px)] px-12 large-desktop:px-16 pt-12 mx-auto max-w-content">
        <div className="flex flex-row">
          <div className="flex flex-col">
            <h1 className="text-4xl font-bold max-w-130">{title}</h1>
            <h2 className="text-secondary/80 font-medium mt-12 text-xl max-w-130">
              {description}
            </h2>
            <div className="mt-10">
              <CTAButton
                onClick={handleClickCTA}
                label={t("getUltimate.ctaDiscount")}
              />
            </div>
          </div>
          <div className="flex flex-col mx-auto">
            <img
              className="max-w-96 pl-8"
              src={"/logo.png"}
              alt={t("getUltimate.logoAlt")}
            />
          </div>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-6 max-w-296">
          {USPs.map((usp) => (
            <USP key={usp.title} {...usp} />
          ))}
        </div>
      </div>
    </main>
  );
};
